import * as t from 'io-ts';

import {parseAccount, RAccount} from './accountModel';
import {makeApiType, makeEnumRuntimeType, RLazyTeammateIdRef} from './apiTypes';
import {
  parseRecipientMatchContact,
  RecipientMatchTypesEnum,
  RRecipientContactMatch,
} from './recipientMatchModel';
import {parseTag, RTag} from './tagModel';

/*
 * Runtime type.
 */

export enum SmartMixClassnamesEnum {
  ACCOUNT = 'account',
  CONTACT = 'contact',
  INBOX = 'inbox',
  MODIFIER = 'modifier',
  PROPERTY = 'property',
  TAG = 'tag',
  TEAMMATE = 'teammate',
}

const RSmartMixClassnames = makeEnumRuntimeType(SmartMixClassnamesEnum);

const RSmartMixBaseMatch = t.readonly(
  t.type({
    class: RSmartMixClassnames,
    primaryLabel: t.string,
    secondaryLabel: t.union([t.string, t.undefined]),
    value: t.string,
  }),
);

const RSmartMixGenericMatch = t.readonly(
  t.intersection([
    RSmartMixBaseMatch,
    t.type({
      class: t.union([
        t.literal(SmartMixClassnamesEnum.INBOX),
        t.literal(SmartMixClassnamesEnum.MODIFIER),
        t.literal(SmartMixClassnamesEnum.PROPERTY),
      ]),
    }),
  ]),
);

const RSmartMixAccountMatch = t.readonly(
  t.intersection([
    RSmartMixBaseMatch,
    t.type({
      class: t.literal(SmartMixClassnamesEnum.ACCOUNT),
      account: RAccount,
    }),
  ]),
);

const RSmartMixContactMatch = t.readonly(
  t.intersection([
    RSmartMixBaseMatch,
    t.type({
      class: t.literal(SmartMixClassnamesEnum.CONTACT),
      recipient: RRecipientContactMatch,
    }),
  ]),
);

const RSmartMixTagMatch = t.readonly(
  t.intersection([
    RSmartMixBaseMatch,
    t.type({
      class: t.literal(SmartMixClassnamesEnum.TAG),
      tag: RTag,
    }),
  ]),
);

const RSmartMixTeammateMatch = t.readonly(
  t.intersection([
    RSmartMixBaseMatch,
    t.type({
      class: t.literal(SmartMixClassnamesEnum.TEAMMATE),
      teammateId: RLazyTeammateIdRef,
    }),
  ]),
);

const RSmartMixMatch = t.union([
  RSmartMixGenericMatch,
  RSmartMixAccountMatch,
  RSmartMixContactMatch,
  RSmartMixTagMatch,
  RSmartMixTeammateMatch,
]);

const RSmartMixMatches = t.readonlyArray(RSmartMixMatch);

/*
 * Type.
 */

interface SmartMixGenericMatchModel extends t.TypeOf<typeof RSmartMixGenericMatch> {}
export interface SmartMixAccountMatchModel extends t.TypeOf<typeof RSmartMixAccountMatch> {}
interface SmartMixContactMatchModel extends t.TypeOf<typeof RSmartMixContactMatch> {}
interface SmartMixTagMatchModel extends t.TypeOf<typeof RSmartMixTagMatch> {}
interface SmartMixTeammateMatchModel extends t.TypeOf<typeof RSmartMixTeammateMatch> {}
export type SmartMixMatchModel =
  | SmartMixContactMatchModel
  | SmartMixAccountMatchModel
  | SmartMixTagMatchModel
  | SmartMixGenericMatchModel
  | SmartMixTeammateMatchModel;

/*
 * IO methods.
 */

function parseSmartMixBaseMatch(src: any) {
  return {
    class: src.class,
    primaryLabel: src.label.primary,
    secondaryLabel: src.label.secondary || undefined,
    value: src.value,
  };
}

function parseSmartMixAccountMatch(src: any): SmartMixAccountMatchModel {
  return {
    ...parseSmartMixBaseMatch(src),
    class: SmartMixClassnamesEnum.ACCOUNT,
    account: parseAccount(src.account),
  };
}

function parseSmartMixContactMatch(src: any): SmartMixContactMatchModel {
  return {
    ...parseSmartMixBaseMatch(src),
    class: SmartMixClassnamesEnum.CONTACT,
    recipient: parseRecipientMatchContact({
      ...src.contact,
      name: src.contact.name || '',
      type: RecipientMatchTypesEnum.CONTACT,
    }),
  };
}

function parseSmartMixTagMatch(src: any): SmartMixTagMatchModel {
  return {
    ...parseSmartMixBaseMatch(src),
    class: SmartMixClassnamesEnum.TAG,
    tag: parseTag(src.tag),
  };
}

function parseSmartMixTeammateMatch(src: any): SmartMixTeammateMatchModel {
  return {
    ...parseSmartMixBaseMatch(src),
    class: SmartMixClassnamesEnum.TEAMMATE,
    teammateId: src.teammate.id,
  };
}

function parseSmartMixMatch(src: any): SmartMixMatchModel {
  switch (src.class) {
    case SmartMixClassnamesEnum.ACCOUNT:
      return parseSmartMixAccountMatch(src);

    case SmartMixClassnamesEnum.CONTACT:
      return parseSmartMixContactMatch(src);

    case SmartMixClassnamesEnum.TAG:
      return parseSmartMixTagMatch(src);

    case SmartMixClassnamesEnum.TEAMMATE:
      return parseSmartMixTeammateMatch(src);

    default:
      return parseSmartMixBaseMatch(src);
  }
}

function parseSmartMixMatches(src: any): ReadonlyArray<SmartMixMatchModel> {
  return (src ?? []).map(parseSmartMixMatch);
}

/*
 * API type.
 */

export const ApiSmartMixMatches = makeApiType('smartMixMatches', RSmartMixMatches, parseSmartMixMatches);
